import { sortBy } from 'lodash';
import react from 'react';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';

import { UserInfiniteList } from '@/workchat/v3/components/UserInfiniteList/UserInfiniteList';
import WorkChatAvatar from '@/workchat/v3/components/WorkChatAvatar/WorkChatAvatar';
import WorkChatListItem from '@/workchat/v3/components/WorkChatListItem/WorkChatListItem';

import '@/workchat/v3/components/ViewConversationV3/ConversationDetailsV3/BigParticipantListV3/BigParticipantListV3.scss';

const USER_PAGE_SIZE = 100;

export interface BigParticipantListV3Props {
  onUsersChange?: (users: UserResponse[]) => void;
}

export default function BigParticipantListV3({ onUsersChange }: BigParticipantListV3Props) {
  const { channel } = useChatContext();

  const [members, setMembers] = react.useState<UserResponse[]>([]);
  const [hasMoreUsers, setHasMoreUsers] = react.useState(false);

  react.useEffect(() => {
    loadMoreUsers(0);
  }, [channel]);

  const loadMoreUsers = async (offset = 0) => {
    try {
      const resp = await channel?.queryMembers({}, { name: 1 }, { limit: USER_PAGE_SIZE, offset });
      const users = resp?.members.map(member => member.user).filter(user => !!user);
      const sortedUsers = sortBy(users ?? [], 'name');

      const updatedMembers = [...members, ...sortedUsers];

      setMembers(updatedMembers);
      setHasMoreUsers(sortedUsers.length === USER_PAGE_SIZE);
      onUsersChange?.(updatedMembers);

      return sortedUsers;
    } catch (_error) {
      // TODO
      //   Sentry.captureException(error, {
      //     tags: {
      //       workchatVersion: 3,
      //       workchatError: WORKCHAT_ERRORS.failedToQueryStreamUsers,
      //     },
      //   });
    }

    return [];
  };

  const userRows: JSX.Element[] = members.map(member => {
    const { id: userId } = member;
    const key = `stream-user-${userId}`;

    return (
      <WorkChatListItem
        key={key}
        hideChevron
        className="user-row"
        testId={key}
        slotPrimary={
          <WorkChatAvatar
            avatarKey={`avatar-stream-user-${userId}`}
            avatarUrl={member!.image}
            firstName={member!.name?.split(' ')[0] || userId}
            lastName={member!.name?.split(' ')[1] || ''}
          />
        }
        slotContent={member!.name || userId}
      />
    );
  });

  return (
    <div className="BigParticipantListV3">
      <UserInfiniteList userRows={userRows} hasMore={hasMoreUsers} loadMore={() => loadMoreUsers(members.length)} />
    </div>
  );
}
